import { getCookie } from 'cookies-next'
import { REGION_COOKIE } from '@/constants/cookies'
import { CANADA, DOMESTIC_REGIONS, UNITED_STATES, UNITED_STATES_AND_TERRITORIES } from '@/constants/regions'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { isDevelopmentEnvironment } from '@/utils/environment-utils'
import { Maybe } from '@/utils/types'

export function getUserRegionFromReq(req: { headers: Record<string, string | string[] | undefined> }): string | null {
  const vercelRegion = getItemOrFirstEntry(req.headers['x-vercel-ip-country'])
  return overrideOrRegion(vercelRegion) ?? null
}

/**
 * Returns the user's state/region code (e.g. "CA", "GA") from Vercel's geo headers.
 * This reads the `x-vercel-ip-country-region` header which is only available on the server.
 * Do not use client side.
 */
export function getUserStateFromReq(req: { headers: Record<string, string | string[] | undefined> }): string | null {
  const state = getItemOrFirstEntry(req.headers['x-vercel-ip-country-region'])
  return state ? state.toUpperCase() : null
}

export function overrideOrRegion(region: Maybe<string>): string | undefined {
  const mockRegion = process.env.NEXT_PUBLIC_MOCK_REGION?.toUpperCase()
  if (isDevelopmentEnvironment() && mockRegion) return mockRegion
  return region?.toUpperCase()
}

export function isUnitedStates(region: Maybe<string>): boolean {
  if (!region) return false
  const upper = region.toUpperCase()
  return Boolean(upper === UNITED_STATES)
}

export function isCanada(region: Maybe<string>): boolean {
  if (!region) return false
  const upper = region.toUpperCase()
  return Boolean(upper === CANADA)
}

export function isUnitedStatesOrCanada(region: Maybe<string>): boolean {
  if (!region) return false
  const upper = region.toUpperCase()
  return Boolean(upper === UNITED_STATES || upper === CANADA)
}

const US_AND_TERRITORIES = new Set(UNITED_STATES_AND_TERRITORIES)
export function isUnitedStatesOrTerritories(region: Maybe<string>): boolean {
  if (!region) return false
  const upper = region.toUpperCase()
  return US_AND_TERRITORIES.has(upper)
}

const DOMESTIC = new Set(DOMESTIC_REGIONS)
export function isDomestic(region: Maybe<string>): boolean {
  if (!region) return false
  const upper = region.toUpperCase()
  return DOMESTIC.has(upper)
}

/**
 * NOTE - Only use this function client side. If used server side, the cookie will always be one request behind.
 * Use getUserRegionFromReq server side.
 * @returns
 */
export function getRegionFromCookie(): string | undefined {
  const region = getCookie(REGION_COOKIE)
  return typeof region === 'string' ? region : undefined
}
